/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import { toHeaderCase } from "js-convert-case";
import config from "../config/credential.config.json";
import _ from "lodash";
import moment from "moment";

import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { GET_CREDENTIAL_DETAIL } from "../graphql";
import { VApplication } from "../../../application";

const parse = require("url-parse");

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

export interface ICredentialMethods extends IPluginMethodMap {
  getCredentialProps(args: any): Promise<any>;
}

function isValidHttpUrl(string: string) {
  let url;

  try {
    url = new URL(string);
  } catch (_) {
    return false;
  }

  return url.protocol === "http:" || url.protocol === "https:";
}

export class CredentialService implements IAgentPlugin {
  readonly methods: ICredentialMethods;

  constructor() {
    this.methods = {
      getCredentialProps: this.getProps.bind(this),
    };
  }

  async getProps(args: any) {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    const url = parse(window.location.href, true);
    let credId;
    let orgId;
    if (url?.query?.tag) {
      credId = url.query.tag;
    }
    if (url?.query?.no) {
      orgId = url.query.no;
    }
    try {
      if (client) {
        const res = await client.query({
          query: GET_CREDENTIAL_DETAIL,
          variables: {
            verifyCredentialPublicId: credId,
          },
          fetchPolicy: "network-only",
          headers: {
            "org-id": orgId,
          },
        });
        if (res?.data) {
          return {
            data: res?.data?.verifyCredentialPublic,
            imageItems: this.formatImages(
              res?.data?.verifyCredentialPublic?.image
            ),
            logoSrc: res?.data?.verifyCredentialPublic?.issuerLogo,
            title: this.formatTitle(res?.data?.verifyCredentialPublic),
            subtitle: this.formatSubTitle(res?.data?.verifyCredentialPublic),
            badgeTitle: res?.data?.verifyCredentialPublic?.status,
            badgeTitleBg: this.formatBadgeTitleBg(
              res?.data?.verifyCredentialPublic
            ),
            description: res?.data?.verifyCredentialPublic?.description,
            credential: this.formatAttributes(
              res?.data?.verifyCredentialPublic
            ),
            attributeItems: this.formatAttributeItems(
              res?.data?.verifyCredentialPublic
            ),
            tableItems: this.formatDigitalSignatures(
              res?.data?.verifyCredentialPublic
            ),
            carouselItems: this.formatCarouselItems(
              res?.data?.verifyCredentialPublic
            ),
            listItemTitle: this.formatGetListItemTitle(),
          };
        }
      }
    } catch (err: any) {
      return err;
    }
  }

  private formatAttributes(credential?: any) {
    let _attributeData: any = [];
    let formatted: any = { ...(credential?.credentialSubject || {}) };
    if (formatted) {
      for (let key in formatted) {
        if (config?.credentialSubject?.blacklistedKeys?.includes(key)) {
          delete formatted[key];
        }
      }
    }

    Object.entries(formatted).forEach(([key, value]) => {
      _attributeData.push({
        label: toHeaderCase(key ?? ""),
        value: value,
      });
    });
    return _attributeData;
  }

  private formatBadgeTitleBg(credential?: any) {
    let formatted: any = "danger";
    if (credential?.status?.toLowerCase()?.includes("active")) {
      formatted = "success";
    }
    return formatted;
  }

  private formatTitle(credential: any) {
    let formatted = credential?.credentialName ?? "";
    return toHeaderCase(formatted);
  }

  private formatSubTitle(credential: any) {
    let formatted = credential?.issuer ?? "";
    return toHeaderCase(formatted);
  }

  private formatImages(image?: string) {
    const formatted: any = [];
    if (image) {
      formatted.push({
        original: `data:image/png;base64,${image}`,
        thumbnail: `data:image/png;base64,${image}`,
      });
    }
    return formatted;
  }

  private formatGetListItemTitle() {
    return config?.digitalSignatures?.title;
  }

  private formatAttributeItems(credential: any) {
    const formatted: any = {
      title: config?.digitalSignatures?.title,
      info: config?.attributeItems,
    };
    if (credential) {
      for (let credKey in credential) {
        if (credKey === "issuer") {
          const index = _.findIndex(
            formatted.info,
            (info: any) => info.id === "digitalSignedBy"
          );
          if (index > -1) {
            formatted.info[index].value = credential[credKey];
          }
        } else if (credKey === "issuerDid") {
          const index = _.findIndex(
            formatted.info,
            (info: any) => info.id === "signerDID"
          );
          if (index > -1) {
            formatted.info[index].value = credential[credKey];
          }
        } else if (credKey === "issuerPk") {
          const index = _.findIndex(
            formatted.info,
            (info: any) => info.id === "signerPublicKey"
          );
          if (index > -1) {
            formatted.info[index].value = credential[credKey];
          }
        } else if (credKey === "validFrom") {
          const index = _.findIndex(
            formatted.info,
            (info: any) => info.id === "validFrom"
          );
          if (index > -1) {
            formatted.info[index].value = this.formatDate(credential[credKey]);
          }
        } else if (credKey === "validUntil") {
          const index = _.findIndex(
            formatted.info,
            (info: any) => info.id === "validUntil"
          );
          if (index > -1) {
            formatted.info[index].value = this.formatDate(credential[credKey]);
          }
        } else if (credKey === "status") {
          const index = _.findIndex(
            formatted.info,
            (info: any) => info.id === "status"
          );
          if (index > -1) {
            formatted.info[index].value = credential[credKey];
          }
        } else {
        }
      }
    }
    return formatted?.info;
  }

  private formatDigitalSignatures(credential: any) {
    const formatted: any = {
      title: config?.digitalSignatures?.title,
      info: config?.digitalSignatures?.info,
    };
    if (credential) {
      for (let credKey in credential) {
        if (credKey === "issuer") {
          const index = _.findIndex(
            formatted.info,
            (info: any) => info.id === "digitalSignedBy"
          );
          if (index > -1) {
            formatted.info[index].value = credential[credKey];
          }
        } else if (credKey === "issuerDid") {
          const index = _.findIndex(
            formatted.info,
            (info: any) => info.id === "signerDID"
          );
          if (index > -1) {
            formatted.info[index].value = credential[credKey];
          }
        } else if (credKey === "issuerPk") {
          const index = _.findIndex(
            formatted.info,
            (info: any) => info.id === "signerPublicKey"
          );
          if (index > -1) {
            formatted.info[index].value = credential[credKey];
          }
        } else if (credKey === "validFrom") {
          const index = _.findIndex(
            formatted.info,
            (info: any) => info.id === "validFrom"
          );
          if (index > -1) {
            formatted.info[index].value = this.formatDate(credential[credKey]);
          }
        } else if (credKey === "validUntil") {
          const index = _.findIndex(
            formatted.info,
            (info: any) => info.id === "validUntil"
          );
          if (index > -1) {
            formatted.info[index].value = this.formatDate(credential[credKey]);
          }
        } else if (credKey === "status") {
          const index = _.findIndex(
            formatted.info,
            (info: any) => info.id === "status"
          );
          if (index > -1) {
            formatted.info[index].value = credential[credKey];
          }
        } else {
        }
      }
    }
    return formatted?.info;
  }

  private formatDate(date: string) {
    if (date) {
      return moment(date).format("lll");
    } else {
      return "Perpetual";
    }
  }

  private formatCarouselItems(credential: any) {
    let _docs: any = [];
    if (credential?.pdf) {
      const src = isValidHttpUrl(credential?.pdf)
        ? credential?.pdf
        : `data:application/pdf;base64,${credential?.pdf}`;
      _docs.push({
        type: "pdf",
        poster: credential?.logo,
        src: src,
        title: credential?.credentialName,
      });
    }
    Array.isArray(credential?.evidence) &&
      credential?.evidence.map((item: any) => {
        let _type =item?.evidenceDocumentType?.split("/");
        _docs.push({
          type: _type[0] ==="image" ? "img": _type[0] ==="video" ? "video" : _type[0] ==="audio" ? "audio": "pdf", 
          poster: item?.poster,
          src: item?.id,
          title: item?.evidenceDocument?.replace(/\.[^/.]+$/, ""),
        });
      });
    return _docs;
  }
}
